import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import { fetchClimate, fetchFires, fetchPointClimate } from "./api";
import {
  reference,
  rowFor,
  trajectory,
  type AreaClimate,
  type ClimateRow,
  type Horizon,
  type IndicatorId,
  type AreaFires,
  type PointClimate,
  type TrajectoryPoint,
} from "./climate";
import type { PriceLevel } from "../map/levels";

export interface AreaClimateResult {
  isLoading: boolean;
  isError: boolean;
  data: AreaClimate | null;
  /** The 1976–2005 reference period — what the projections are a change from. */
  reference: ClimateRow | null;
  /** The chosen scenario and horizon. */
  projected: ClimateRow | null;
  /** The chosen indicator over time, with the cross-scenario spread as a band. */
  path: TrajectoryPoint[];
}

/**
 * One area's climate, with the currently chosen view already derived.
 *
 * Every scenario and horizon arrives in one response, so switching any of the
 * controls is a recompute over data already in memory — the same contract the
 * map layers keep, for the same reason.
 */
export function useAreaClimate(
  area: { scope: PriceLevel; code: string } | null,
  view: { indicator: IndicatorId; scenario: string; horizon: Horizon },
): AreaClimateResult {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["climate", area?.scope, area?.code],
    queryFn: () => fetchClimate(area!.scope, area!.code),
    enabled: area !== null,
    staleTime: Infinity,
    retry: false,
  });

  const ref = useMemo(() => (data ? reference(data.series) ?? null : null), [data]);
  const projected = useMemo(
    () => (data ? rowFor(data.series, view.scenario, view.horizon) ?? null : null),
    [data, view.scenario, view.horizon],
  );
  const path = useMemo(
    () => (data ? trajectory(data.series, view.indicator, view.scenario) : []),
    [data, view.indicator, view.scenario],
  );

  return {
    isLoading: area !== null && isLoading,
    isError,
    data: data ?? null,
    reference: ref,
    projected,
    path,
  };
}

export interface PointClimateResult {
  isLoading: boolean;
  isError: boolean;
  data: PointClimate | null;
}

/**
 * The heat-island block under a point, and the water restriction in force.
 *
 * `staleTime` is finite here, unlike everything else in this app: the
 * restriction half of the answer is today's, and the whole value of it is that
 * it is current.
 */
export function usePointClimate(
  point: { lon: number; lat: number } | null,
): PointClimateResult {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["climate", "point", point?.lon.toFixed(5), point?.lat.toFixed(5)],
    queryFn: () => fetchPointClimate(point!.lon, point!.lat),
    enabled: point !== null,
    staleTime: 30 * 60_000,
    retry: false,
  });

  return {
    isLoading: point !== null && isLoading,
    isError,
    data: data ?? null,
  };
}


export interface AreaFiresResult {
  isLoading: boolean;
  isError: boolean;
  data: AreaFires | null;
}

/** One area's recorded fire history. */
export function useAreaFires(
  area: { scope: PriceLevel; code: string } | null,
): AreaFiresResult {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["climate", "fires", area?.scope, area?.code],
    queryFn: () => fetchFires(area!.scope, area!.code),
    enabled: area !== null,
    staleTime: Infinity,
    retry: false,
  });

  return {
    isLoading: area !== null && isLoading,
    isError,
    data: data ?? null,
  };
}
